// Beeldmerk voor in een jsPDF-document: gedeeld door de labopdracht, de boorprofielen-export
// en het projectrapport.
import logoUrl from '../assets/logo-full.png';
import { PAGE } from './pdf-page.js';

/**
 * Een beeldmerk klaarmaken voor de PDF: de data-URL en zijn hoogte/breedte-verhouding.
 *
 * Zonder bron valt hij terug op het beeldmerk van GeoVoice. Mislukt het laden -- een kapotte
 * data-URI, of een omgeving zonder fetch -- dan levert hij null en gaat de PDF gewoon door.
 * Een formulier zonder beeldmerk is ongemakkelijk; een formulier dat niet gemaakt wordt erger.
 *
 * @param {string|null} [source]  data-URI; undefined = GeoVoice, null/'' = geen beeldmerk
 * @returns {Promise<{data: string, ratio: number, format: string}|null>}  ratio = hoogte/breedte
 */
export async function loadPdfLogo(source) {
  if (source === null || source === '') return null;
  try {
    let data = source;
    if (!data) {
      const blob = await (await fetch(logoUrl)).blob();
      data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }
    if (typeof data !== 'string' || !data.startsWith('data:image/')) return null;
    const size = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.width, h: img.height });
      img.onerror = reject;
      img.src = data;
    });
    if (!size.w || !size.h) return null;
    const type = /^data:image\/([a-z0-9+.-]+)/i.exec(data)?.[1]?.toUpperCase() || 'PNG';
    return { data, ratio: size.h / size.w, format: type === 'JPG' ? 'JPEG' : type };
  } catch (_) {
    return null;
  }
}

/** Het bedrijfslogo, en anders dat van GeoVoice (ook als het bedrijfslogo niet te laden is). */
export async function loadCompanyOrBrandLogo(companyLogo) {
  return (companyLogo ? await loadPdfLogo(companyLogo) : null) || loadPdfLogo();
}

/**
 * Zet het beeldmerk rechts uitgelijnd in een vak van maxW × maxH mm, verhouding behouden.
 * Eén alias per beeldmerk, zodat jsPDF het maar één keer opslaat, hoeveel pagina's er ook zijn.
 * @returns {number} de gebruikte breedte in mm (0 zonder beeldmerk)
 */
export function drawLogoRight(doc, logo, rightX, topY, maxW, maxH) {
  if (!logo) return 0;
  const h = Math.min(maxH, maxW * logo.ratio);
  const w = h / logo.ratio;
  doc.addImage(logo.data, logo.format, rightX - w, topY + (maxH - h) / 2, w, h, 'pdf-logo', 'FAST');
  return w;
}

// ─── Paginakop en -voet (staand A4) ──────────────────────────────────────────
// Gedeeld door het projectrapport en de boorprofielen-export, zodat beide er hetzelfde uitzien:
// titel links, project en logo (hoogstens 10 mm) rechts boven een lijn; datum en paginanummer
// onderaan.

const HEADER_LOGO_MAX_W = 40, HEADER_LOGO_MAX_H = 10;

function textStyle(doc, size, weight, col) {
  doc.setFontSize(size);
  doc.setFont('helvetica', weight);
  doc.setTextColor(...col);
}

/** Kop: `title` links, `label` (project) en het logo rechts, boven een donkerblauwe lijn. */
export function stampPageHeader(doc, { title, label, logo }) {
  const { PW, ML, MR, MT } = PAGE;
  doc.setDrawColor(22, 48, 90); doc.setLineWidth(0.5);
  doc.line(ML, MT - 3, PW - MR, MT - 3);
  const logoW = drawLogoRight(doc, logo, PW - MR, MT - 4 - HEADER_LOGO_MAX_H, HEADER_LOGO_MAX_W, HEADER_LOGO_MAX_H);
  textStyle(doc, 6.5, 'bold', [22, 48, 90]);
  doc.text(title, ML, MT - 6);
  if (label) {
    textStyle(doc, 6.5, 'normal', [130, 140, 155]);
    doc.text(label, PW - MR - logoW - (logoW ? 4 : 0), MT - 6, { align: 'right' });
  }
}

/** Voet: datum links, "p / total" rechts. */
/** Voet: datum links, paginanummer rechts, en optioneel een regel in het midden (de schaal). */
export function stampPageFooter(doc, p, total, midden = '') {
  const { PW, PH, ML, MR, MB } = PAGE;
  const fy = PH - MB + 3;
  doc.setDrawColor(210, 215, 222); doc.setLineWidth(0.25);
  doc.line(ML, fy - 2, PW - MR, fy - 2);
  textStyle(doc, 6.5, 'normal', [155, 160, 170]);
  doc.text(new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' }), ML, fy + 1.5);
  doc.text(`${p} / ${total}`, PW - MR, fy + 1.5, { align: 'right' });
  if (midden) doc.text(midden, PW / 2, fy + 1.5, { align: 'center' });
}
